import type { LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";

type IconButtonProps = Omit<
  ComponentProps<typeof Button>,
  "aria-label" | "children" | "size" | "variant"
> & {
  badge?: string | number;
  icon: LucideIcon;
  iconClassName?: string;
  label: string;
  size?: "sm" | "xs";
  tooltipSide?: ComponentProps<typeof TooltipContent>["side"];
};

export function IconButton({
  badge,
  className,
  icon: Icon,
  iconClassName,
  label,
  size = "sm",
  tooltipSide = "bottom",
  ...props
}: IconButtonProps) {
  const accessibleLabel = badge === undefined ? label : `${label} (${badge})`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          {...props}
          className={cn(
            "rounded-(--icon-button-radius)",
            badge !== undefined && "relative",
            className,
          )}
          size={size === "xs" ? "icon-xs" : "icon-sm"}
          variant="icon"
          aria-label={accessibleLabel}
        >
          <Icon className={iconClassName} strokeWidth={size === "xs" ? 1.5 : 2} />
          {badge !== undefined && (
            <Badge
              aria-hidden="true"
              variant="secondary"
              size="xs"
              className="pointer-events-none absolute -end-1 -top-1.5"
            >
              {badge}
            </Badge>
          )}
        </Button>
      </TooltipTrigger>
      {/* The tooltip only repeats the label. Touch devices show it whenever a tap or dialog focuses
          the button, so it is hidden there; the aria-label still names the button. */}
      <TooltipContent side={tooltipSide} sideOffset={6} className="pointer-coarse:hidden">
        {accessibleLabel}
      </TooltipContent>
    </Tooltip>
  );
}
